import { AnimationGroup } from "@babylonjs/core";

/**
 * AnimationSystem – simple state-machine wrapper around Babylon AnimationGroups.
 *
 * Register each animation with a string key, then call play() to switch
 * between them. Only one animation runs at a time; any previously playing
 * animation is stopped before the new one starts.
 */
export class AnimationSystem {
  private registry = new Map<string, AnimationGroup | null>();
  private currentKey = "";

  register(key: string, group: AnimationGroup | null): void {
    this.registry.set(key, group);
  }

  /**
   * Start (or continue) the animation identified by `key`.
   *
   * @param key    The name used in register().
   * @param loop   Whether the animation should loop.
   * @param speed  Playback speed multiplier (default 1).
   */
  play(key: string, loop: boolean, speed = 1): void {
    const anim = this.registry.get(key);
    if (!anim) return;

    // Skip if the same looping animation is already playing.
    if (this.currentKey === key && (anim.isPlaying || !loop)) return;

    // Stop every other registered animation.
    this.registry.forEach((a, k) => {
      if (a && k !== key && a.isPlaying) a.stop();
    });

    this.currentKey = key;
    anim.start(loop, speed, anim.from, anim.to, false);
  }

  stop(key: string): void {
    this.registry.get(key)?.stop();
    if (this.currentKey === key) this.currentKey = "";
  }

  get current(): string {
    return this.currentKey;
  }

  /** Force a reset so the next play() call always restarts. */
  resetCurrent(): void {
    this.currentKey = "";
  }
}
